/**
 * 平台与浏览器识别。
 *
 * 纯函数（UA 从参数传入）是为了能测——真机上跑不到的分支，测试里必须跑得到。
 *
 * ⚠️ 铁律：**识别结果只许用来"解释"，绝不许用来"门禁"。**
 * 能不能用某个能力一律靠 feature detection；UA 只决定给用户看哪一段安装步骤、
 * 哪一句提示。iPadOS 桌面模式会把自己报成 macOS，靠 UA 做门禁必然误伤。
 */
/**
 * iOS 上所有浏览器底层都是 WebKit，UA 里都有 "like Mac OS X"；靠各家自己加的标记区分：
 * Chrome→CriOS、Edge→EdgiOS、Firefox→FxiOS；都没有才是 Safari。
 */
export function detectBrowser(ua = '', hints = {}) {
    if (isIOSUa(ua, hints)) {
        if (/CriOS/.test(ua))
            return 'ios-chrome';
        if (/EdgiOS/.test(ua))
            return 'ios-edge';
        if (/FxiOS/.test(ua))
            return 'ios-firefox';
        return 'ios-safari';
    }
    if (/Android/.test(ua))
        return 'android';
    return 'desktop';
}
export function detectOS(ua = '', hints = {}) {
    if (isIOSUa(ua, hints))
        return 'ios';
    if (/Android/.test(ua))
        return 'android';
    if (/Windows NT/.test(ua))
        return 'windows';
    if (/Mac OS X/.test(ua))
        return 'macos';
    if (/Linux|X11|CrOS/.test(ua))
        return 'linux';
    return 'unknown';
}
/** 桌面模式的 iPad 会把自己报成 macOS；带触点的 MacIntel 只可能是 iPad。 */
function isIOSUa(ua, hints) {
    if (/iPad|iPhone|iPod/.test(ua))
        return true;
    return hints.platform === 'MacIntel' && (hints.maxTouchPoints ?? 0) > 1;
}
export function isIOSBrowser(kind) {
    return kind.startsWith('ios-');
}
/**
 * iOS 主版本号。取不到就返回 null —— 而 **null 必须按"新版本"处理**：
 * 桌面模式的 iPad、以及未来改了 UA 格式的版本都会落在这里，
 * 按"旧版本"处理等于凭空给现代设备关掉功能。
 */
export function iosMajorVersion(ua = '') {
    const m = /(?:iPhone )?OS (\d+)[._]/.exec(ua);
    if (m?.[1])
        return Number(m[1]);
    // 桌面模式 iPad 的 UA 里没有 "OS x_y"，只有 "Version/17.4 Safari"，
    // 那既可能是 iPad 也可能是真 Mac —— 分不清就返回 null（= 按新版本处理）。
    return null;
}
export function describePlatform(ua = '', hints = {}) {
    const browser = detectBrowser(ua, hints);
    const os = detectOS(ua, hints);
    return {
        browser,
        os,
        isIOS: os === 'ios',
        isAndroid: os === 'android',
        isDesktop: browser === 'desktop',
        iosVersion: os === 'ios' ? iosMajorVersion(ua) : null,
    };
}
export function currentPlatform() {
    if (typeof navigator === 'undefined')
        return describePlatform('');
    return describePlatform(navigator.userAgent || '', {
        maxTouchPoints: navigator.maxTouchPoints || 0,
        platform: navigator.platform || '',
    });
}
//# sourceMappingURL=platform.js.map